import Select, { createFilter } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import styles from './Dropdown.module.scss';
import {
  getDefaultSelectStyles,
  makeBorderedSelectStyles,
  dropdownSizes,
} from './Dropdown.constants';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { TooltipWrapper } from '../TooltipWrapper';
import { SvgIcon, SvgIconSrc } from '../SvgIcon';
const LEFT_ICON_PADDING_STYLES = {
  singleValue: { paddingLeft: '38px' },
  placeholder: { paddingLeft: '38px' },
  input: { paddingLeft: '38px' },
};

// Extends react-select's default filtering so options can also be matched
// against a hidden `searchText` field (e.g. country names for timezones),
// without changing the visible label or behavior for options that don't set it.
const filterOption = createFilter({
  stringify: (option) =>
    `${option.label} ${option.value} ${option.data?.searchText || ''}`,
});

// Per-slot merge, not a flat object spread — a flat spread lets a later
// object's e.g. `singleValue` silently replace an earlier object's
// `singleValue` wholesale instead of merging the two, which would drop
// LEFT_ICON_PADDING_STYLES' padding the moment a caller also overrides
// `singleValue`/`placeholder`/`input` themselves.
const mergeSelectStyles = (...styleObjects) => {
  const merged = {};
  for (const styleObject of styleObjects) {
    if (!styleObject) continue;
    for (const slot of Object.keys(styleObject)) {
      merged[slot] = { ...merged[slot], ...styleObject[slot] };
    }
  }
  return merged;
};

export const DropdownComponent = ({
  options,
  value,
  onChange,
  isLoading,
  isDisabled,
  classNamePrefix,
  selectStyles,
  label,
  errorMessage,
  placeholder,
  isMulti,
  width,
  tooltip,
  isCreatable,
  onCreateOption,
  leftIcon,
  size,
  bordered = false,
  isSearchable = true,
  formatOptionLabel,
}) => {
  const SelectComponent = isCreatable ? CreatableSelect : Select;
  const sizeStyles = size
    ? makeBorderedSelectStyles(size)
    : bordered
      ? makeBorderedSelectStyles(dropdownSizes.md)
      : null;
  const mergedSelectStyles = mergeSelectStyles(
    sizeStyles,
    leftIcon ? LEFT_ICON_PADDING_STYLES : null,
    selectStyles
  );
  return (
    <div className={styles.dropdownContainer} style={{ width }}>
      {label && (
        <div className={styles.labelContainer}>
          <Text
            variant={textVariantsEnum.Text_13}
            fontWeight={fontWeightEnum.Medium}
            className={styles.label}
          >
            {label}
          </Text>
          {tooltip && (
            <TooltipWrapper tooltip={tooltip} wrapperClassName="mb-2">
              <SvgIcon src={SvgIconSrc.info} color="gray-400" size={16} />
            </TooltipWrapper>
          )}
        </div>
      )}
      <div className={styles.selectWrapper}>
        {leftIcon && <span className={styles.selectLeftIcon}>{leftIcon}</span>}
        <SelectComponent
          isMulti={isMulti}
          options={options}
          menuPortalTarget={
            typeof window !== 'undefined' ? document.body : null
          }
          value={value}
          onChange={onChange}
          menuPosition="fixed"
          filterOption={filterOption}
          isLoading={isLoading}
          isDisabled={isDisabled}
          isSearchable={isSearchable}
          placeholder={placeholder}
          classNamePrefix={classNamePrefix}
          onCreateOption={onCreateOption}
          styles={getDefaultSelectStyles(mergedSelectStyles)}
          formatOptionLabel={formatOptionLabel}
        />
      </div>
      {!!errorMessage && (
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Regular}
          color="error-500"
        >
          {errorMessage}
        </Text>
      )}
    </div>
  );
};
